import React, { useMemo, useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import dialogStreak from "../../assets/img/1 APP INTRO & FIRST LAUNCH/dialog-login-streak.svg";
import icFireTitle from "../../assets/img/1 APP INTRO & FIRST LAUNCH/ic-fire-title.svg";
import icFireSmall from "../../assets/img/1 APP INTRO & FIRST LAUNCH/ic-fire-small.svg";
import claimEgg from "../../assets/img/1 APP INTRO & FIRST LAUNCH/claim-egg-4x.webp";
import { CutButton, ButtonLabel, DialogCard } from "./IntroPrimitives";
import {
  claimDailyStreak,
  loadActiveStreak,
  streakWeekDays,
} from "../../apiCalls/streaks/api_v1_streaks";

export default function LoginStreak({ onClaim }) {
  const [streak, setStreak] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loaded, setLoaded] = useState(() => !sessionStorage.getItem("accessToken"));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!sessionStorage.getItem("accessToken")) return;
      try {
        const next = await loadActiveStreak({ force: true });
        if (!cancelled && next) setStreak(next);
      } catch (error) {
        console.error("Active streak load failed:", error);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const canClaim =
    loaded &&
    Boolean(sessionStorage.getItem("accessToken")) &&
    !streak?.rewardClaimed;
  const claimAmount = streak?.amountToClaim || 1;
  const headerDay = streak?.rewardClaimed
    ? Math.max(1, streak.activeStreak || 1)
    : Math.max(1, streak?.streakToClaim || streak?.displayDay || 1);

  const handleClaim = async (event) => {
    if (!loaded || submitting) return;
    if (!canClaim) {
      event?.preventDefault?.();
      onClaim(Boolean(streak?.rewardClaimed));
      return;
    }
    setSubmitting(true);
    try {
      const next = await claimDailyStreak();
      if (next?.success) {
        setStreak(next);
        onClaim(true);
        return;
      }
      const refreshed = await loadActiveStreak({ force: true });
      if (refreshed?.rewardClaimed) {
        setStreak(refreshed);
        onClaim(true);
      }
    } catch (error) {
      console.error("Daily streak update failed:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const days = useMemo(
    () =>
      streakWeekDays(
        streak || {
          activeStreak: 0,
          streakToClaim: 1,
          rewardClaimed: false,
        }
      ),
    [streak]
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        bgcolor: "#0A0B08",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: "16px", md: "20px" },
        py: 3,
        boxSizing: "border-box",
      }}
    >
      <DialogCard
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "100%",
          height: "auto",
          px: { xs: "16px", md: "40px" },
          py: { xs: "26px", md: "40px" },
        }}
      >
        <Box component="img" className="dialog-bg" src={dialogStreak} alt="" />
        <Typography
          sx={{
            fontFamily: "'Rajdhani', sans-serif",
            fontWeight: 700,
            fontSize: { xs: 12, md: 14 },
            letterSpacing: { xs: "6px", md: "3px" },
            color: "#B8EE3C",
            position: "relative",
            lineHeight: "15px",
          }}
        >
          DAILY STREAK
        </Typography>
        <Box sx={{ height: { xs: 8, md: 16 }, width: "100%" }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", position: "relative" }}>
          <Box component="img" src={icFireTitle} alt="" sx={{ width: 19, height: 19 }} />
          <Typography
            sx={{
              fontFamily: "'Rajdhani', sans-serif",
              fontWeight: 700,
              fontSize: { xs: 22, md: 30 },
              letterSpacing: "1.5px",
              color: "#FFFFFF",
              lineHeight: "28px",
            }}
          >
            DAY
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Rajdhani', sans-serif",
              fontWeight: 700,
              fontSize: { xs: 22, md: 30 },
              letterSpacing: "1.5px",
              color: "#B8EE3C",
              lineHeight: "28px",
            }}
          >
            {headerDay}
          </Typography>
        </Box>
        <Box sx={{ height: { xs: 14, md: 24 }, width: "100%" }} />
        <Box
          sx={{
            display: "flex",
            gap: { xs: "6px", md: "8px" },
            justifyContent: "center",
            width: "100%",
            position: "relative",
            overflow: "visible",
          }}
        >
          {days.map((day) => (
            <Box
              key={day.dateKey || day.day}
              sx={{
                width: { xs: 40, md: 68 },
                height: { xs: 48, md: 86 },
                flexShrink: 0,
                background: "#0D0F0A",
                border:
                  day.state === "today"
                    ? "1px solid #B8EE3C"
                    : day.state === "done"
                      ? "1px solid #F9A825"
                      : "1px solid #262B1C",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "2px",
              }}
            >
              {day.state === "locked" ? (
                <Typography sx={{ color: "#666B5C", fontSize: 13, lineHeight: "20px" }}>·</Typography>
              ) : (
                <Box component="img" src={icFireSmall} alt="" sx={{ width: 11, height: 11 }} />
              )}
              <Typography
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: { xs: 7.5, md: 10 },
                  color: "#666B5C",
                  lineHeight: "11px",
                  whiteSpace: "nowrap",
                }}
              >
                DAY {day.day}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ height: { xs: 12, md: 20 }, width: "100%" }} />
        <Typography
          sx={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: { xs: 11.5, md: 14 },
            lineHeight: "17px",
            color: "#9AA88A",
            textAlign: "center",
            width: "100%",
            position: "relative",
          }}
        >
          Streak alive! Come back daily
        </Typography>
        <Box sx={{ height: { xs: 14, md: 24 }, width: "100%" }} />
        <CutButton
          wide
          onClick={handleClaim}
          disabled={!loaded || submitting}
        style={{
            position: "relative",
            opacity: !loaded || submitting ? 0.7 : 1,
            cursor: !loaded || submitting ? "default" : "pointer",
          }}
        >
          <ButtonLabel wide>
            {submitting ? "CLAIMING" : canClaim ? `CLAIM +${claimAmount}` : "CONTINUE"}
          </ButtonLabel>
          {canClaim && (
            <Box
              component="img"
              src={claimEgg}
              alt=""
              sx={{ width: 15, height: 18, position: "relative", zIndex: 1 }}
            />
          )}
        </CutButton>
      </DialogCard>
    </Box>
  );
}
